import { JerseyNumber } from "../../components/rink/JerseyNumber";
import { useI18n } from "../../i18n/I18nProvider";
import { usageLevel } from "./usage-level";

export interface UsageBarProps {
  label: string;
  used: number;
  /** Omitted or zero when the resource has no quota. */
  quota?: number;
  format: (value: number) => string;
  /** Word after the reading, e.g. "players". */
  unit?: string;
  /** Text shown instead of the bar when there is no quota. */
  fallback: string;
}

/**
 * A rink-length bar: the track is the quota, the blue fill is use, and the red goal line at the end
 * marks the limit. From 90 percent the fill turns red and a one-line note appears.
 */
export function UsageBar({ label, used, quota, format, unit, fallback }: UsageBarProps) {
  const { t } = useI18n();
  if (quota === undefined || quota <= 0) {
    return (
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <h2 className="text-operate font-semibold text-ink">{label}</h2>
        <p className="text-note text-ink-2">{fallback}</p>
      </div>
    );
  }
  const level = usageLevel(used, quota);
  const reading = `${format(used)} / ${format(quota)}`;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-1">
        <h2 className="text-operate font-semibold text-ink">{label}</h2>
        <p className="flex items-baseline gap-2">
          <JerseyNumber value={reading} size="md" />
          {unit ? <span className="text-note text-ink-2">{unit}</span> : null}
        </p>
      </div>
      <div
        role="meter"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={quota}
        aria-valuenow={used}
        aria-valuetext={reading}
        className="relative h-3 rounded-sm bg-ice-2"
      >
        <div
          aria-hidden="true"
          className={`absolute inset-y-0 left-0 rounded-sm transition-[width] duration-[240ms] ease-rink motion-reduce:transition-none ${
            level.nearLimit ? "bg-red" : "bg-blue"
          }`}
          style={{ width: `${level.fraction * 100}%` }}
        />
        <span aria-hidden="true" className="absolute -inset-y-1 right-0 w-[3px] bg-red" />
      </div>
      {level.nearLimit ? (
        <p className="text-note font-semibold text-red">{t("usage.nearLimit")}</p>
      ) : null}
    </div>
  );
}
